import { useState, useEffect, Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  User,
  LogOut,
  Bell,
  Building2,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useApiToken } from "@/components/providers/token-provider";
import { useDisplayableApplications } from "@/hooks/api";
import DevOrgSwitcher from "@/components/dev/DevOrgSwitcher";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Loader } from "@/components/ui/loader";
import { NotificationBell } from "@/components/notifications";
import { useDocuments, useDocumentAccess } from "@/hooks/api";
import { getFileTypeIcon } from "@/utils/format";

const urbintLogoWhite = "/logos/urbint/Urbint Logo - White Target Only.png";

const navItems = [
  {
    title: "Home",
    href: "/",
  },
];

// Full-page navigation (window.location, not React Router) for the user-menu
// destinations. NOT a workaround for auth/org state: those are correct.
//
// Reason: navigating from /applications/hip via React Router's `navigate()`
// updates `pushState` (the URL bar changes) but <Routes> never re-renders.
// HIPApp's many data hooks (useStorms, useStormMetadata, useStormScenario,
// useTrackGeoJSON, useStormImpacts, etc.) keep firing high-priority renders
// that interrupt BrowserRouter's internal location-update transition before
// it commits. Result: URL bar shows /organization, page still shows HIP.
// We verified `navigate(path, { flushSync: true })` does NOT resolve it on
// react-router-dom 7.13. window.location.href bypasses the router entirely
// and always navigates, at the cost of a full reload. Acceptable tradeoff
// for these infrequent top-level user-menu destinations.
//
// If HIPApp's re-render churn is reduced (or react-router-dom fixes the
// transition-preemption race), revert this to `navigate(path)`.
const goToFullPage = (path: string) => () => {
  window.location.href = path;
};

// User button with logout
function UserButton() {
  const { logout, user } = useApiToken();
  const [documentationOpen, setDocumentationOpen] = useState(false);
  const { data: documents, loading: documentsLoading, error: documentsError, refetch: refetchDocuments } = useDocuments(true);
  const { execute: openDocument, loading: accessLoading } = useDocumentAccess();

  const handleLogout = async () => {
    // logout() is back-channel only: it revokes the refresh token (terminating
    // the KC session server-side), clears cookies + React auth state, and the
    // ProtectedRoute redirects to /login once isAuthenticated flips to false.
    // No full-page navigation to Keycloak.
    await logout();
  };

  const handleDocumentClick = async (e: Event, documentId: string) => {
    e.preventDefault();
    await openDocument(documentId);
  };

  const displayName = user
    ? `${user.first_name} ${user.last_name}`.trim()
    : "User";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full bg-white/10 hover:bg-white/20"
        >
          <User className="h-5 w-5 text-white" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem disabled>
          <div className="flex flex-col">
            <span className="font-medium">{displayName}</span>
            {user?.email && (
              <span className="text-xs text-muted-foreground">
                {user.email}
              </span>
            )}
          </div>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer" onSelect={goToFullPage("/organization")}>
          <Building2 className="mr-2 h-4 w-4" />
          Org Settings
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer" onSelect={goToFullPage("/profile")}>
          <User className="mr-2 h-4 w-4" />
          Manage Profile
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer" onSelect={goToFullPage("/settings/notifications")}>
          <Bell className="mr-2 h-4 w-4" />
          Alert Settings
        </DropdownMenuItem>
        <DropdownMenuSub open={documentationOpen} onOpenChange={setDocumentationOpen}>
          <DropdownMenuSubTrigger className="cursor-pointer gap-2 [&_svg]:shrink-0 [&_svg]:size-4">
            <FileText className="mr-2 h-4 w-4" />
            Documentation
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-64 max-h-[min(24rem,70vh)] overflow-y-auto">
            <TooltipProvider delayDuration={300}>
              {documentsLoading ? (
                <div className="p-4 flex items-center justify-center">
                  <Loader size="sm" text="Loading documents..." />
                </div>
              ) : documentsError ? (
                <div className="p-4 text-center">
                  <p className="text-sm text-destructive mb-2">
                    Unable to load documents
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refetchDocuments()}
                  >
                    Retry
                  </Button>
                </div>
              ) : documents.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No documents available
                </div>
              ) : (
                documents.map((doc) => {
                  const FileIcon = getFileTypeIcon(doc.file_type);
                  return (
                    <DropdownMenuItem
                      key={doc.id}
                      className="cursor-pointer"
                      onSelect={(e) => handleDocumentClick(e, doc.id)}
                      disabled={accessLoading}
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center min-w-0 w-full">
                            <FileIcon className="mr-2 h-4 w-4 shrink-0" />
                            <span className="font-medium truncate">
                              {doc.title}
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="w-64 max-w-xs">
                          {doc.description || "No description"}
                        </TooltipContent>
                      </Tooltip>
                    </DropdownMenuItem>
                  );
                })
              )}
            </TooltipProvider>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();

  const { data: availableApplications, loading: applicationsLoading } =
    useDisplayableApplications();

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleAppClick = (href: string, enabled: boolean, id?: string) => {
    if (enabled) {
      if (id) {
        localStorage.setItem("current_application_id", id);
      }
      navigate(href);
    }
  };

  return (
    <header
      className="sticky top-0 z-50 w-full border-b backdrop-blur-sm"
      style={{ backgroundColor: "var(--midnight-blue)" }}
    >
      <div className="w-full flex h-16 items-center justify-between gap-3 px-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          <Link to="/" className="flex shrink-0 items-center">
            {mounted ? (
              <img
                src={urbintLogoWhite}
                alt="Urbint Logo"
                className="h-10 w-10 shrink-0 object-contain"
              />
            ) : (
              <div className="h-10 w-10 shrink-0 animate-pulse rounded-lg bg-muted/20" />
            )}
          </Link>
          <div className="flex h-8 shrink-0 items-center">
            <div className="h-full w-px bg-border" />
          </div>
          <div className="flex h-8 min-w-0 items-center sm:hidden">
            <p className="whitespace-nowrap text-sm font-medium text-white">
              <abbr
                title="Emergency Preparedness & Response"
                className="cursor-help no-underline"
              >
                EP&amp;R
              </abbr>
            </p>
          </div>
          <div className="hidden h-8 min-w-0 items-center sm:flex">
            <p className="truncate text-sm font-medium text-white">
              EMERGENCY PREPAREDNESS & RESPONSE
            </p>
          </div>
        </div>

        {/* Desktop navigation */}
        <nav className="hidden md:flex items-center gap-6">
          <DevOrgSwitcher />
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="text-sm font-medium text-white transition-colors hover:text-gray-200"
            >
              {item.title}
            </Link>
          ))}

          {/* Applications Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-white transition-colors hover:text-gray-200 outline-none">
              Applications <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {applicationsLoading ? (
                <div className="p-4 text-center text-xs text-muted-foreground animate-pulse">
                  Loading applications...
                </div>
              ) : availableApplications && availableApplications.length > 0 ? (
                availableApplications.map((app, index) => (
                  <Fragment key={app.id || app.slug}>
                    <DropdownMenuItem
                      className={
                        app.enabled
                          ? "cursor-pointer"
                          : "opacity-50 cursor-not-allowed"
                      }
                      onSelect={() =>
                        handleAppClick(
                          `/applications/${app.slug}`,
                          app.enabled,
                          app.id
                        )
                      }
                      disabled={!app.enabled}
                    >
                      <div className="flex flex-col w-full">
                        <div className="flex items-center justify-between">
                          <span
                            className={`font-medium ${
                              !app.enabled ? "text-muted-foreground" : ""
                            }`}
                          >
                            {app.name}
                          </span>
                          {!app.enabled && (
                            <span className="text-xs bg-muted text-muted-foreground px-1 py-0.5 rounded">
                              Disabled
                            </span>
                          )}
                        </div>
                        <span
                          className={`text-xs ${
                            app.enabled
                              ? "text-muted-foreground"
                              : "text-muted-foreground/70"
                          }`}
                        >
                          {app.description}
                        </span>
                      </div>
                    </DropdownMenuItem>

                    {index < availableApplications.length - 1 && (
                      <DropdownMenuSeparator />
                    )}
                  </Fragment>
                ))
              ) : (
                <div className="p-4 text-center text-xs text-muted-foreground">
                  No applications available
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Notification Bell */}
          <NotificationBell />

          {/* User section */}
          <UserButton />
        </nav>

        {/* Mobile navbar actions */}
        <div className="flex shrink-0 items-center gap-2 md:hidden">
          <NotificationBell />
          <UserButton />
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/10"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Mobile menu — dark surface: header uses --midnight-blue always; theme popover is light in :root */}
      <div
        className={cn(
          "fixed inset-x-0 top-16 z-50 border-b border-white/10 text-white shadow-md md:hidden",
          mobileMenuOpen ? "block" : "hidden"
        )}
        style={{ backgroundColor: "var(--midnight-blue)" }}
      >
        <div className="container max-h-[calc(100dvh-4rem)] overflow-y-auto px-4 py-4">
          <nav className="flex flex-col gap-4">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="text-sm font-medium text-white transition-colors hover:text-gray-200"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.title}
              </Link>
            ))}

            <div className="pt-2 pb-1">
              <h3 className="text-sm font-medium text-white">Applications</h3>
            </div>

            {applicationsLoading ? (
              <div className="py-2 pl-2 text-xs text-white/70">
                Loading...
              </div>
            ) : availableApplications && availableApplications.length > 0 ? (
              availableApplications.map((app) =>
                app.enabled ? (
                  <Link
                    key={app.id || app.slug}
                    to={`/applications/${app.slug}`}
                    className="border-l-2 border-white/25 pl-2 transition-colors hover:border-primary/50"
                    onClick={() => {
                      if (app.id) {
                        localStorage.setItem("current_application_id", app.id);
                      }
                      setMobileMenuOpen(false);
                    }}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-white">
                        {app.name}
                      </span>
                      <span className="text-xs text-white/70">{app.description}</span>
                    </div>
                  </Link>
                ) : (
                  <div
                    key={app.id || app.slug}
                    className="border-l-2 border-white/25 pl-2 opacity-50"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-white">
                        {app.name}
                      </span>
                      <span className="text-xs text-white/70">{app.description}</span>
                    </div>
                  </div>
                )
              )
            ) : (
              <div className="py-2 pl-2 text-xs text-white/70">
                No applications available
              </div>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}
